import { Globe2Icon, ImageIcon, PaletteIcon, SaveIcon, Trash2Icon } from "lucide-react";
import type { ChangeEvent, SyntheticEvent } from "react";

import type { WorkspaceAdminResponse } from "../../../shared/schemas";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Field, FieldDescription, FieldError, FieldLabel } from "../../components/ui/field";
import { Input } from "../../components/ui/input";

interface WorkspaceAppearancePanelProps {
  colorError: string;
  colorPending: boolean;
  faviconPending: boolean;
  isOnline: boolean;
  logoPending: boolean;
  mutationMessage: string | null;
  organizationId: string;
  primaryColor: string;
  primaryColorPickerValue: string;
  workspace: WorkspaceAdminResponse;
  onColorChange: (value: string) => void;
  onFaviconRemove: () => void;
  onFaviconUpload: (file: File) => void;
  onLogoRemove: () => void;
  onLogoUpload: (file: File) => void;
  onResetColor: () => void;
  onSaveColor: (event: SyntheticEvent<HTMLFormElement>) => void;
}

function ColorCard(props: WorkspaceAppearancePanelProps) {
  return (
    <Card className="lg:col-span-2">
      <CardHeader className="border-b">
        <div className="grid size-10 place-items-center rounded-sm border-[1.5px] border-primary bg-info-background text-primary">
          <PaletteIcon aria-hidden="true" className="size-5" />
        </div>
        <CardTitle>Primary color</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-5" onSubmit={props.onSaveColor}>
          <Field data-invalid={Boolean(props.colorError)}>
            <FieldLabel htmlFor="workspace-primary-color">Primary color</FieldLabel>
            <div className="flex max-w-md items-center gap-3">
              <input
                aria-label="Choose primary color"
                className="h-11 w-14 shrink-0 cursor-pointer rounded-sm border bg-card p-1"
                disabled={props.colorPending || !props.isOnline}
                id="workspace-primary-color-picker"
                type="color"
                value={props.primaryColorPickerValue}
                onChange={(event) => {
                  props.onColorChange(event.target.value);
                }}
              />
              <Input
                aria-invalid={Boolean(props.colorError)}
                autoCapitalize="none"
                disabled={props.colorPending || !props.isOnline}
                id="workspace-primary-color"
                maxLength={7}
                spellCheck={false}
                value={props.primaryColor}
                onChange={(event) => {
                  props.onColorChange(event.target.value);
                }}
              />
            </div>
            <FieldDescription>
              Buttons, links, highlights, and focus indicators adapt to this color in both light and
              dark mode.
            </FieldDescription>
            <FieldError>{props.colorError}</FieldError>
          </Field>
          <div className="flex flex-wrap gap-3">
            <Button disabled={props.colorPending || !props.isOnline} type="submit">
              <SaveIcon aria-hidden="true" data-icon="inline-start" /> Save color
            </Button>
            {props.workspace.organization.primaryColor ? (
              <Button
                disabled={props.colorPending || !props.isOnline}
                type="button"
                variant="outline"
                onClick={props.onResetColor}
              >
                Use CCBox blue
              </Button>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function readUpload(event: ChangeEvent<HTMLInputElement>, upload: (file: File) => void): void {
  const file = event.target.files?.[0];
  if (file) upload(file);
  event.target.value = "";
}

function ImageCard({
  accept,
  description,
  emptyText,
  id,
  image,
  imageClassName,
  isOnline,
  pending,
  title,
  onRemove,
  onUpload,
}: {
  accept: string;
  description: string;
  emptyText: string;
  id: string;
  image: { alt: string; src: string } | null;
  imageClassName: string;
  isOnline: boolean;
  pending: boolean;
  title: string;
  onRemove: () => void;
  onUpload: (file: File) => void;
}) {
  const Icon = id === "workspace-logo" ? ImageIcon : Globe2Icon;
  return (
    <Card>
      <CardHeader className="border-b">
        <div className="grid size-10 place-items-center rounded-sm border-[1.5px] border-primary bg-info-background text-primary">
          <Icon aria-hidden="true" className="size-5" />
        </div>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-5">
        <div className="grid min-h-28 place-items-center rounded-lg border bg-background p-5">
          {image ? (
            <img alt={image.alt} className={imageClassName} src={image.src} />
          ) : (
            <div className="text-center text-sm text-muted-foreground">{emptyText}</div>
          )}
        </div>
        <Field>
          <FieldLabel htmlFor={id}>
            Upload a {id === "workspace-logo" ? "logo" : "favicon"}
          </FieldLabel>
          <Input
            accept={accept}
            disabled={pending || !isOnline}
            id={id}
            type="file"
            onChange={(event) => {
              readUpload(event, onUpload);
            }}
          />
          <FieldDescription>{description}</FieldDescription>
        </Field>
        {image ? (
          <Button
            className="justify-self-start"
            disabled={pending || !isOnline}
            type="button"
            variant="outline"
            onClick={onRemove}
          >
            <Trash2Icon aria-hidden="true" data-icon="inline-start" /> Remove{" "}
            {id === "workspace-logo" ? "logo" : "favicon"}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function WorkspaceAppearancePanel(props: WorkspaceAppearancePanelProps) {
  const organization = props.workspace.organization;
  const assetRoot = `/api/workspaces/${encodeURIComponent(props.organizationId)}`;
  return (
    <section
      aria-labelledby="workspace-appearance-heading"
      className="grid gap-5 py-8 lg:grid-cols-2"
    >
      <div className="lg:col-span-2">
        <h2
          className="m-0 text-balance font-heading text-2xl font-bold"
          id="workspace-appearance-heading"
        >
          Workspace appearance
        </h2>
        <p className="mt-2 mb-0 text-pretty text-muted-foreground">
          Choose how this workspace looks on its sign-in page and throughout CCBox.
        </p>
      </div>
      <ColorCard {...props} />
      <ImageCard
        accept="image/png,image/jpeg,image/webp,image/avif"
        description="PNG, JPEG, WebP, or AVIF. Maximum file size: 512 KB."
        emptyText="No workspace logo uploaded"
        id="workspace-logo"
        image={
          organization.logoVersion
            ? {
                alt: `${organization.name} logo`,
                src: `${assetRoot}/logo?v=${organization.logoVersion}`,
              }
            : null
        }
        imageClassName="max-h-20 max-w-full object-contain"
        isOnline={props.isOnline}
        pending={props.logoPending}
        title="Workspace logo"
        onRemove={props.onLogoRemove}
        onUpload={props.onLogoUpload}
      />
      <ImageCard
        accept="image/png,image/jpeg,image/webp,image/avif,image/x-icon,image/vnd.microsoft.icon,.ico"
        description="PNG, JPEG, WebP, AVIF, or ICO. Maximum file size: 512 KB."
        emptyText="Using the CCBox favicon"
        id="workspace-favicon"
        image={
          organization.faviconVersion
            ? {
                alt: `${organization.name} favicon`,
                src: `${assetRoot}/favicon?v=${organization.faviconVersion}`,
              }
            : null
        }
        imageClassName="size-16 rounded-sm object-contain outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
        isOnline={props.isOnline}
        pending={props.faviconPending}
        title="Workspace favicon"
        onRemove={props.onFaviconRemove}
        onUpload={props.onFaviconUpload}
      />
      {props.mutationMessage ? (
        <Alert className="lg:col-span-2" role="alert" variant="destructive">
          <AlertDescription>{props.mutationMessage}</AlertDescription>
        </Alert>
      ) : null}
    </section>
  );
}
